import { useEffect, useRef, useState } from "react";
import api, { API_BASE, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  FileText, Image as ImageIcon, FileSpreadsheet, File as FileIcon,
  Upload, Trash2, Download, Loader2, Paperclip,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { hasCapability, formatDate } from "@/lib/permissions";

const ACCEPT = ".pdf,.jpg,.jpeg,.png,.docx,.xlsx";
const MAX_BYTES = 10 * 1024 * 1024;

/**
 * Documents & attachments panel for an asset — used inside AssetDetail as a tab.
 * Drag-and-drop upload area + list with download / delete.
 */
export default function AssetAttachments({ assetId }) {
  const { user } = useAuth();
  const canUpload = hasCapability(user, "upload_asset_attachment");
  const canDelete = hasCapability(user, "delete_asset_attachment");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/assets/${assetId}/attachments`);
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not load attachments");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [assetId]); // eslint-disable-line react-hooks/exhaustive-deps

  const doUpload = async (file) => {
    if (file.size > MAX_BYTES) {
      toast.error(`${file.name} exceeds 10 MB limit`);
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/assets/${assetId}/attachments`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Uploaded ${file.name}`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Upload failed");
    } finally { setUploading(false); }
  };

  const onPickedFiles = async (files) => {
    for (const f of Array.from(files || [])) {
      await doUpload(f);
    }
  };

  const download = async (a) => {
    try {
      const token = localStorage.getItem("assetiq_token");
      const res = await fetch(`${API_BASE}/assets/${assetId}/attachments/${a.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = a.filename;
      link.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Download failed");
    }
  };

  const del = async (a) => {
    if (!window.confirm(`Delete ${a.filename}? This cannot be undone.`)) return;
    try {
      await api.delete(`/assets/${assetId}/attachments/${a.id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Delete failed");
    }
  };

  return (
    <div className="space-y-4" data-testid="attachments-panel">
      {canUpload && (
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            onPickedFiles(e.dataTransfer.files);
          }}
          onClick={() => inputRef.current?.click()}
          className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors
            ${dragOver ? "border-[#7C1D3A] bg-[#fcf0f3]" : "border-slate-300 hover:border-slate-400 bg-slate-50"}`}
          data-testid="attachment-dropzone"
        >
          <Upload className={`w-8 h-8 mx-auto mb-2 ${dragOver ? "text-[#7C1D3A]" : "text-slate-400"}`} />
          <div className="font-medium text-slate-700 text-sm">
            {uploading ? "Uploading…" : "Drop files here or click to browse"}
          </div>
          <div className="text-xs text-slate-500 mt-1">
            PDF, JPG, PNG, DOCX, XLSX · Max 10 MB per file · Up to 20 files per asset
          </div>
          <input
            ref={inputRef}
            type="file"
            multiple
            accept={ACCEPT}
            className="hidden"
            onChange={(e) => onPickedFiles(e.target.files)}
            data-testid="attachment-file-input"
          />
        </div>
      )}

      {loading ? (
        <div className="p-8 flex items-center justify-center text-slate-400">
          <Loader2 className="w-5 h-5 animate-spin" />
        </div>
      ) : items.length === 0 ? (
        <div className="p-8 text-center text-sm text-slate-400 border border-slate-200 rounded-lg">
          <Paperclip className="w-8 h-8 mx-auto mb-2 opacity-40" />
          No documents attached yet. {canUpload && "Drop an invoice, warranty card, or manual above."}
        </div>
      ) : (
        <div className="border border-slate-200 rounded-lg divide-y divide-slate-100" data-testid="attachments-list">
          {items.map((a) => (
            <AttachmentRow
              key={a.id}
              attachment={a}
              canDelete={canDelete}
              onDownload={() => download(a)}
              onDelete={() => del(a)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function AttachmentRow({ attachment, canDelete, onDownload, onDelete }) {
  const Icon = iconFor(attachment.extension);
  return (
    <div className="p-3 flex items-center gap-3" data-testid={`attachment-${attachment.id}`}>
      <div className="w-9 h-9 rounded-md bg-slate-100 text-slate-500 flex items-center justify-center shrink-0">
        <Icon className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-medium text-sm text-slate-900 truncate">{attachment.filename}</div>
        <div className="text-xs text-slate-500 flex items-center gap-2">
          <span>{formatSize(attachment.size_bytes)}</span>
          <span>·</span>
          <span>uploaded {formatRel(attachment.uploaded_at)}</span>
          {attachment.uploaded_by_name && (<><span>·</span><span>by {attachment.uploaded_by_name}</span></>)}
        </div>
      </div>
      <Button variant="ghost" size="sm" onClick={onDownload} data-testid={`download-${attachment.id}`}>
        <Download className="w-4 h-4" />
      </Button>
      {canDelete && (
        <Button variant="ghost" size="sm" onClick={onDelete} className="text-slate-400 hover:text-red-600" data-testid={`delete-${attachment.id}`}>
          <Trash2 className="w-4 h-4" />
        </Button>
      )}
    </div>
  );
}

function iconFor(ext) {
  const e = (ext || "").toLowerCase();
  if (e === ".pdf") return FileText;
  if (e === ".jpg" || e === ".jpeg" || e === ".png") return ImageIcon;
  if (e === ".xlsx") return FileSpreadsheet;
  if (e === ".docx") return FileText;
  return FileIcon;
}

function formatSize(b) {
  if (!b) return "";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
}

function formatRel(iso) {
  if (!iso) return "";
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return formatDate(iso);
}
